import React from 'react'
import {
  HiOutlineAdjustments,
  HiOutlineArrowsExpand,
  HiOutlineBadgeCheck,
  HiOutlineCash,
  HiOutlineCalendar,
  HiOutlineChartSquareBar,
  HiOutlineCog,
  HiOutlineCurrencyDollar,
  HiOutlineFolder,
  HiOutlineInformationCircle,
  HiOutlineLink,
  HiOutlineLightBulb,
  HiOutlineRefresh,
  HiOutlineSaveAs,
  HiOutlineSearch,
  HiOutlineShare,
  HiOutlineShieldCheck,
  HiOutlineTrendingUp,
  HiOutlineUserGroup,
  HiOutlineUsers,
} from 'react-icons/hi'
import { ANALYTICAL_JOURNEY_STAGES } from '../../components/analytical_journey/analyticalJourney'
import { GOVERNANCE_DECISION_JOURNEY_STAGES } from '../../components/governance_journey/governanceDecisionJourney'

export const WORKSPACE_KINDS = Object.freeze({
  SIMPLE: 'simple',
  COMPOSITE: 'composite',
})

export const SIDEBAR_JOURNEYS = Object.freeze({
  ANALYTICAL: 'analytical',
  GOVERNANCE: 'governance',
})

const DECISION_JOURNEY_ROUTES = new Set([
  ...ANALYTICAL_JOURNEY_STAGES.map(({ route }) => route),
  ...GOVERNANCE_DECISION_JOURNEY_STAGES.map(({ route }) => route),
])

// Journey definitions own process semantics; the capability's canonical section
// determines which of the two separate Sidebar rails presents that membership.
export function getSidebarJourneyMembership(link) {
  if (!DECISION_JOURNEY_ROUTES.has(link.path)) return null
  if (link.section === 'Investment Governance') return SIDEBAR_JOURNEYS.GOVERNANCE
  if (link.section === 'Risk & Analytics') return SIDEBAR_JOURNEYS.ANALYTICAL
  return null
}

export function createSimpleWorkspace({ key, label, path, icon, capability, legacySection, legacyPlacement = 'main' }) {
  return {
    key,
    label,
    kind: WORKSPACE_KINDS.SIMPLE,
    ...(capability ? { capabilities: [capability] } : { icon, path }),
    legacySection,
    legacyPlacement,
  }
}

export function createCompositeWorkspace({ key, label, capabilities, legacySection }) {
  return {
    key,
    label,
    kind: WORKSPACE_KINDS.COMPOSITE,
    capabilities,
    legacySection,
    legacyPlacement: 'main',
  }
}

function capability(key, label, path, icon, legacySection) {
  return { key, label, path, icon, legacySection }
}

export const WORKSPACE_NAVIGATION = Object.freeze([
  createCompositeWorkspace({
    key: 'executive',
    label: 'Executive',
    legacySection: 'Executive',
    capabilities: [
      capability('board-dashboard', 'Board Dashboard', '/board-dashboard', <HiOutlineChartSquareBar />, 'Executive'),
      capability('executive-dashboard', 'Executive Dashboard', '/executive-dashboard', <HiOutlineTrendingUp />, 'Executive'),
      capability('strategic-analysis', 'Risk Analysis Workspace', '/strategic-analysis', <HiOutlineShieldCheck />, 'Executive'),
    ],
  }),
  createCompositeWorkspace({
    key: 'investment-governance',
    label: 'Investment Governance',
    legacySection: 'Investment Governance',
    capabilities: [
      capability('execution-tracker', 'Execution Tracker', '/mitigation-tracker', <HiOutlineCalendar />, 'Investment Governance'),
      capability('outcome-measurement', 'Outcome Measurement', '/benefits-governance', <HiOutlineBadgeCheck />, 'Investment Governance'),
      capability('investment-monitoring', 'Program Control Tower', '/investment-monitoring', <HiOutlineSearch />, 'Investment Governance'),
      capability('board-decisions', 'Board Decisions', '/board-decisions', <HiOutlineUserGroup />, 'Investment Governance'),
      capability('investment-governance', 'Investment Governance', '/investment-governance', <HiOutlineLink />, 'Investment Governance'),
      capability('decision-center', 'Portfolio Capital Strategy', '/decision-center', <HiOutlineArrowsExpand />, 'Investment Governance'),
    ],
  }),
  createCompositeWorkspace({
    key: 'risk-analytics',
    label: 'Risk & Analytics',
    legacySection: 'Risk & Analytics',
    capabilities: [
      capability('portfolio-analysis', 'Portfolio Analysis', '/portfolio-analysis', <HiOutlineLightBulb />, 'Risk & Analytics'),
      capability('investment-optimization', 'Risk Investment Optimization', '/investment-optimization', <HiOutlineRefresh />, 'Risk & Analytics'),
      capability('sensitivity-analysis', 'Sensitivity Analysis', '/sensitivity', <HiOutlineAdjustments />, 'Risk & Analytics'),
      capability('risk-quantification', 'Risk Quantification', '/risk-quantification', <HiOutlineCurrencyDollar />, 'Risk & Analytics'),
      capability('risk-financing', 'Risk Financing', '/risk-financing', <HiOutlineCash />, 'Risk & Analytics'),
    ],
  }),
  createCompositeWorkspace({
    key: 'data-scenarios',
    label: 'Data & Scenarios',
    legacySection: 'Data & Scenarios',
    capabilities: [
      capability('fair-scenarios', 'Risk Scenarios', '/fair-scenarios', <HiOutlineSaveAs />, 'Data & Scenarios'),
      capability('risk-dependencies', 'Risk Dependencies', '/risk-dependencies', <HiOutlineShare />, 'Data & Scenarios'),
      capability('risk-data', 'Scenario Data', '/risk-data', <HiOutlineFolder />, 'Data & Scenarios'),
    ],
  }),
  createCompositeWorkspace({
    key: 'administration',
    label: 'Administration',
    legacySection: 'Administration',
    capabilities: [
      capability('administration-users', 'Users', '/administration/users', <HiOutlineUsers />, 'Administration'),
      capability('administration-roles', 'Roles & Permissions', '/administration/roles', <HiOutlineShieldCheck />, 'Administration'),
      capability('governance-framework', 'Governance Framework', '/governance-framework', <HiOutlineInformationCircle />, 'Administration'),
      capability('platform-settings', 'Platform Settings', '/platform-settings', <HiOutlineCog />, 'Administration'),
    ],
  }),
])

function projectWorkspace(workspace) {
  if (workspace.kind === WORKSPACE_KINDS.SIMPLE) {
    if (workspace.capabilities) {
      return workspace.capabilities.map(({ legacySection, ...item }) => ({
        ...item,
        section: legacySection,
      }))
    }

    return [{
      key: workspace.key,
      label: workspace.label,
      path: workspace.path,
      icon: workspace.icon,
      ...(workspace.legacySection ? { section: workspace.legacySection } : {}),
    }]
  }

  return workspace.capabilities.map(({ legacySection, ...item }) => ({
    ...item,
    section: legacySection,
  }))
}

// These projections are the temporary boundary between the canonical model and
// the Sidebar's current flat main/bottom link collections.
export function projectLegacySidebarLinks(navigation = WORKSPACE_NAVIGATION) {
  return navigation
    .filter((workspace) => workspace.legacyPlacement === 'main')
    .flatMap(projectWorkspace)
}

export function projectLegacySidebarBottomLinks(navigation = WORKSPACE_NAVIGATION) {
  return navigation
    .filter((workspace) => workspace.legacyPlacement === 'bottom')
    .flatMap(projectWorkspace)
}

const ACTIVE_ROUTE_OWNERS = Object.freeze({
  'investment-governance': [
    '/investment-governance',
    '/investment-projects',
    '/investment-proposals',
    '/board/investment-proposals',
    '/investment-programs',
    '/program-execution',
  ],
  'investment-monitoring': ['/investment-monitoring'],
  'outcome-measurement': ['/benefits-governance'],
  'board-decisions': ['/board-decisions'],
  'risk-data': ['/risk-data'],
})

export function isWorkspaceLinkActive(link, pathname) {
  const ownedPaths = ACTIVE_ROUTE_OWNERS[link.key] || [link.path]
  return ownedPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}
